import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { clampDims, type HomeWidget } from "@ronin/contracts";
import { HOME_GRID_COLUMNS } from "@ronin/core";
import { cn } from "@ronin/ui";
import { MoveDiagonal2 } from "lucide-react";
import { type KeyboardEvent, type PointerEvent, useRef, useState } from "react";
import { BoardsWidget } from "./boards-nav.js";
import { ClockWidget } from "./clock-widget.js";
import { ContainerTile } from "./container-widget.js";
import { LinkContent, LinkTile } from "./link-tile.js";
import { ServerWidget } from "./server-widget.js";

/**
 * FR9: tile spans for the reading-order layout below lg (4 / 8 columns): wider tiles are
 * capped at the columns there. Rows are 0.5rem. Literal class names, so Tailwind
 * finds them.
 */
const colSpans = [
  "",
  "col-span-1",
  "col-span-2",
  "col-span-3",
  "col-span-4",
  "col-span-4 md:col-span-5",
  "col-span-4 md:col-span-6",
  "col-span-4 md:col-span-7",
  "col-span-4 md:col-span-8",
  "col-span-4 md:col-span-8",
  "col-span-4 md:col-span-8",
  "col-span-4 md:col-span-8",
  "col-span-4 md:col-span-8",
];
const rowSpans = [
  "",
  "row-span-1",
  "row-span-2",
  "row-span-3",
  "row-span-4",
  "row-span-5",
  "row-span-6",
  "row-span-7",
  "row-span-8",
  "row-span-9",
  "row-span-10",
  "row-span-11",
  "row-span-12",
  "row-span-13",
  "row-span-14",
  "row-span-15",
  "row-span-16",
  "row-span-17",
  "row-span-18",
  "row-span-19",
  "row-span-20",
  "row-span-21",
  "row-span-22",
  "row-span-23",
  "row-span-24",
];

export const spanOf = (widget: Pick<HomeWidget, "w" | "h">) =>
  cn(colSpans[widget.w] ?? "col-span-2", rowSpans[widget.h] ?? "row-span-4");

export const widgetLabel = (widget: HomeWidget) =>
  widget.type === "link" || widget.type === "container"
    ? widget.config.name
    : widget.type === "heading"
      ? widget.config.text
      : { clock: "Clock", divider: "Divider", boards: "Boards", server: "Server" }[widget.type];

/** Widgets that never have a tile background (only their content). */
const bare = (widget: HomeWidget) =>
  widget.hideBackground || widget.type === "divider" || widget.type === "boards";

/** A heading grows with its tile's height (half rows). */
const headingSize = (h: number) => (h <= 2 ? "text-sm" : h <= 4 ? "text-lg" : "text-xl");

export const tileClass =
  "relative min-w-0 overflow-hidden border border-border/60 bg-card/60 backdrop-blur-sm";

/** The tile's content, also used by the drag overlay. */
export function WidgetContent({ widget, live }: { widget: HomeWidget; live: boolean }) {
  if (widget.type === "clock")
    return <ClockWidget config={widget.config} x={widget.x} w={widget.w} h={widget.h} />;
  if (widget.type === "divider") {
    return (
      <div className="flex h-full items-center px-1" aria-hidden>
        <span className="w-full border-t border-foreground/80" />
      </div>
    );
  }
  if (widget.type === "boards") {
    return <BoardsWidget live={live} h={widget.h} />;
  }
  if (widget.type === "container")
    return <ContainerTile config={widget.config} live={live} w={widget.w} h={widget.h} />;
  if (widget.type === "server")
    return <ServerWidget w={widget.w} h={widget.h} config={widget.config} />;
  if (widget.type === "heading") {
    return (
      <div className="flex h-full min-w-0 items-end px-3 pb-2">
        <h2
          className={cn(
            "truncate font-semibold tracking-tight text-foreground",
            headingSize(widget.h),
          )}
        >
          {widget.config.text}
        </h2>
      </div>
    );
  }
  if (live) return <LinkTile config={widget.config} />;
  return (
    <div className="flex h-full min-w-0 items-center gap-3 px-3">
      <LinkContent config={widget.config} />
    </div>
  );
}

/**
 * A widget on the board. In edit mode (FR8) the whole tile drags (free grid only), a click or
 * Enter opens its settings, × deletes, and the corner handle resizes by whole cells within the
 * type's limits (drag it, or arrow keys on it).
 */
export function WidgetTile({
  widget,
  editing,
  free,
  onEdit,
  onResize,
  onDelete,
}: {
  widget: HomeWidget;
  editing: boolean;
  /** On the free grid (from lg) the tile sits on its own cells and can be dragged. */
  free: boolean;
  onEdit: () => void;
  onResize: (dims: Dims) => void;
  onDelete: () => void;
}) {
  const draggable = useDraggable({ id: widget.id, disabled: !editing || !free });
  const [preview, setPreview] = useState<Dims | null>(null);
  const shown = { ...widget, ...preview } as HomeWidget;
  const label = widgetLabel(widget);
  const { w, h } = shown;
  // Through the CSSOM (React style), which the CSP allows.
  const place = free
    ? { gridColumn: `${widget.x + 1} / span ${w}`, gridRow: `${widget.y + 1} / span ${h}` }
    : undefined;
  const span = free ? undefined : spanOf(shown);

  if (!editing) {
    return (
      <section
        aria-label={label}
        style={place}
        className={cn(
          tileClass,
          span,
          // FR11b: only the content; the tile itself is invisible.
          bare(widget)
            ? "border-transparent bg-transparent backdrop-blur-none"
            : "hover:border-input",
        )}
      >
        <WidgetContent widget={shown} live />
      </section>
    );
  }

  return (
    <section
      ref={draggable.setNodeRef}
      aria-label={label}
      style={{ ...place, transform: CSS.Translate.toString(draggable.transform) }}
      className={cn(
        tileClass,
        span,
        "border-dashed border-input",
        // In edit mode a background-less tile keeps its dashed outline so it can be found.
        bare(widget) && "bg-transparent backdrop-blur-none",
        draggable.isDragging && "z-20 cursor-grabbing border-solid border-ring bg-card/90",
        preview && "border-ring",
      )}
    >
      <button
        type="button"
        {...draggable.attributes}
        {...draggable.listeners}
        aria-label={`Edit ${label}`}
        // Keyboard clicks (detail 0) also follow Space, which drags; Enter is handled below.
        onClick={(event) => event.detail !== 0 && onEdit()}
        onKeyDown={(event) => {
          draggable.listeners?.onKeyDown?.(event);
          if (event.key === "Enter") {
            event.preventDefault();
            onEdit();
          }
          // FR8: Delete (or Backspace) removes the focused tile; a toast offers Undo.
          if ((event.key === "Delete" || event.key === "Backspace") && widget.type !== "boards") {
            event.preventDefault();
            onDelete();
          }
        }}
        className={cn(
          "block h-full w-full text-left touch-manipulation focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
          free && "cursor-grab",
        )}
      >
        <WidgetContent widget={shown} live={false} />
      </button>
      <ResizeEdges
        widget={widget}
        label={label}
        onPreview={setPreview}
        onResize={(dims) => {
          setPreview(null);
          if (dims.w !== widget.w || dims.h !== widget.h) onResize(dims);
        }}
      />
    </section>
  );
}

interface Dims {
  w: number;
  h: number;
}

/** The grid's column width, row height and gap, measured from the tile's grid. */
function measure(tile: HTMLElement) {
  const grid = tile.closest<HTMLElement>("[data-tile-grid]");
  const style = grid ? getComputedStyle(grid) : undefined;
  const column = Number.parseFloat(style?.gridTemplateColumns.split(" ")[0] ?? "") || 80;
  const row = Number.parseFloat(style?.gridAutoRows ?? "") || 8;
  const gap = Number.parseFloat(style?.columnGap ?? "") || 8;
  return { column, row, gap };
}

const keySteps: Record<string, Dims> = {
  ArrowRight: { w: 1, h: 0 },
  ArrowLeft: { w: -1, h: 0 },
  ArrowDown: { w: 0, h: 1 },
  ArrowUp: { w: 0, h: -1 },
};

type Axis = "x" | "y" | "xy";

/** Where each edge sits and how the pointer looks over it. */
const edges: Array<{ axis: Axis; className: string }> = [
  { axis: "x", className: "top-0 right-0 bottom-3 w-2 cursor-ew-resize" },
  { axis: "y", className: "right-3 bottom-0 left-0 h-2 cursor-ns-resize" },
  { axis: "xy", className: "right-0 bottom-0 size-3 cursor-nwse-resize" },
];

/**
 * spec12 FR9: resize by dragging the tile's right edge (width), bottom edge (height) or
 * bottom-right corner (both), in whole cells within the type's limits; an edge lights up under
 * the pointer. Keyboard: a resize button that shows when focused (Tab), arrow keys step by a
 * cell.
 */
function ResizeEdges({
  widget,
  label,
  onPreview,
  onResize,
}: {
  widget: HomeWidget;
  label: string;
  onPreview: (dims: Dims | null) => void;
  onResize: (dims: Dims) => void;
}) {
  const drag = useRef<{
    axis: Axis;
    x: number;
    y: number;
    width: number;
    height: number;
    dims: Dims;
  } | null>(null);
  // FR9: never past the grid's right edge.
  const bounded = (dims: Dims) => {
    const clamped = clampDims(widget.type, dims);
    return { ...clamped, w: Math.min(clamped.w, HOME_GRID_COLUMNS - widget.x) };
  };

  function onPointerDown(event: PointerEvent<HTMLSpanElement>, axis: Axis) {
    // The tile itself is draggable; the edges resize instead.
    event.stopPropagation();
    const tile = event.currentTarget.closest("section");
    if (!tile) return;
    const rect = tile.getBoundingClientRect();
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = {
      axis,
      x: event.clientX,
      y: event.clientY,
      width: rect.width,
      height: rect.height,
      dims: { w: widget.w, h: widget.h },
    };
  }

  function onPointerMove(event: PointerEvent<HTMLSpanElement>) {
    const state = drag.current;
    const tile = event.currentTarget.closest("section");
    if (!state || !tile) return;
    const { column, row, gap } = measure(tile);
    state.dims = bounded({
      w:
        state.axis === "y"
          ? widget.w
          : (state.width + event.clientX - state.x + gap) / (column + gap),
      h:
        state.axis === "x"
          ? widget.h
          : (state.height + event.clientY - state.y + gap) / (row + gap),
    });
    onPreview(state.dims);
  }

  function onPointerUp() {
    const state = drag.current;
    drag.current = null;
    if (state) onResize(state.dims);
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    // Keys on the resize button never reach the tile's drag and drop.
    event.stopPropagation();
    const step = keySteps[event.key];
    if (!step) return;
    event.preventDefault();
    onResize(bounded({ w: widget.w + step.w, h: widget.h + step.h }));
  }

  return (
    <>
      {edges.map(({ axis, className }) => (
        <span
          key={axis}
          aria-hidden
          onPointerDown={(event) => onPointerDown(event, axis)}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={() => {
            drag.current = null;
            onPreview(null);
          }}
          className={cn(
            "absolute z-10 touch-none transition-colors duration-150 hover:bg-ring/60",
            className,
          )}
        />
      ))}
      <button
        type="button"
        aria-label={`Resize ${label}, now ${widget.w} columns by ${widget.h} rows. Arrow keys change the size.`}
        onKeyDown={onKeyDown}
        className="pointer-events-none absolute right-0 bottom-0 z-20 flex size-6 items-center justify-center bg-popover text-foreground opacity-0 focus-visible:opacity-100 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
      >
        <MoveDiagonal2 className="size-3.5" aria-hidden />
      </button>
    </>
  );
}
